/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { createContext, useContext, useMemo, useState } from 'react';
import { usePush, type Push } from './usePush';
import { PushDialog } from './PushDialog';
import { PushIntro } from './PushIntro';

/**
 * One answer to "is push on", shared by everything that asks.
 *
 * **Why a context and not three calls to `usePush()`.** Three things need this:
 * the switch in the account menu, the dialog behind it, and the one-time
 * introduction — and they are in different corners of the tree. Given a hook
 * each they would hold three independent copies of the same state, so turning
 * push on from the introduction would leave the menu's switch reading *off*
 * until the page was reloaded. There is exactly one browser and exactly one
 * subscription; there is exactly one piece of state.
 *
 * The dialog and the introduction are rendered here rather than by their
 * callers, for the same reason: whoever opens the dialog is not where it belongs
 * on screen, and the introduction has no caller at all — it decides for itself.
 */
interface PushUi extends Push {
  /** Open the explanation. The switch calls this wherever it cannot act. */
  explain: () => void;
}

const Ctx = createContext<PushUi | null>(null);

/**
 * Read the shared push state.
 *
 * Returns null outside the provider, and callers render nothing rather than
 * throwing — the account menu also appears in tests and screenshots that mount
 * fragments of the tree, and a hard failure there would be a crash in place of
 * one missing row.
 */
export function usePushUi(): PushUi | null {
  return useContext(Ctx);
}

export function PushProvider({ children }: { children: React.ReactNode }) {
  const push = usePush();
  const [dialogOpen, setDialogOpen] = useState(false);

  const value = useMemo<PushUi>(
    () => ({ ...push, explain: () => setDialogOpen(true) }),
    [push],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <PushDialog open={dialogOpen} onOpenChange={setDialogOpen} push={push} />
      {/* Decides for itself whether to appear, and waits for the board before it
          does — see the note in PushIntro. */}
      <PushIntro push={push} onExplain={() => setDialogOpen(true)} />
    </Ctx.Provider>
  );
}
